import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Button, Card, Accordion, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';
import '../styles/landing.css'; 

const BRAND_NAME = 'Mazes AI';
const AGENT_NAME = 'Mazes AI Agent';

export default function LandingPage() {
  const pillars = [
    { title: 'Our Voice, Consistently', text: `${AGENT_NAME} mirrors our tone, values, and style across every conversation.` },
    { title: 'Answers From Our World', text: `Grounded responses come from our approved knowledge—never guesses.` },
    { title: 'Always On-Brand', text: `From product guidance to service updates, ${AGENT_NAME} stays aligned with how ${BRAND_NAME} speaks.` },
    { title: 'Fast & Respectful', text: 'Immediate help that respects context, time, and the person on the other side.' },
    { title: 'Evolves With Us', text: `As ${BRAND_NAME} grows, ${AGENT_NAME} grows—learning from our new materials, not the internet’s noise.` },
    { title: 'Built Responsibly', text: 'Clarity, safety, and accountability guide how we deploy AI internally and publicly.' },
  ];

  const moments = [
    { title: 'Welcoming Visitors', text: `A warm greeting that sounds like ${BRAND_NAME}—not a bot.` },
    { title: 'Explaining Our Offers', text: 'Clear, consistent descriptions of our products and services.' },
    { title: 'Guiding Next Steps', text: 'From “Where do I start?” to “How do I get help?”, the path is simple.' },
    { title: 'Celebrating Wins', text: 'Announcements, milestones, and moments—shared beautifully and on-brand.' },
  ];

  const faqs = [
    { q: `What is ${AGENT_NAME}?`, a: `${AGENT_NAME} is our brand’s AI representative—trained on our content to speak with our voice and standards.` },
    { q: 'Where do its answers come from?', a: 'From our selected knowledge sources—polished materials, approved documents, and our public voice.' },
    { q: 'Is it replacing our team?', a: 'No. It supports our team by handling repetitive questions so people focus on deeper work.' },
    { q: 'How do we keep it responsible?', a: 'We review training sources, monitor tone and accuracy, and improve continuously with feedback.' },
  ];

  return (
    <div className="landing-scope">
      {/* SEO META TAGS */}
      <Helmet>
        <title>MazesAI — Trainable AI Agent for Modern Business</title>
        <meta
          name="description"
          content="MazesAI manages our brand’s activities across every platform — making sales happen, guiding customers, and simplifying how users experience our products."
        />
        <meta
          name="keywords"
          content="MazesAI, AI agent, conversational AI, sales automation, customer support, business assistant, multi-platform AI"
        />
        <meta property="og:title" content="MazesAI — Trainable AI Agent for Modern Business" />
        <meta
          property="og:description"
          content="MazesAI handles our brand’s activities across every platform — making sales happen, guiding customers, and simplifying how users experience our products."
        />
        <meta property="og:url" content="https://mazesai.com" />
        <meta property="og:image" content="/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="MazesAI — Trainable AI Agent" />
        <meta
          name="twitter:description"
          content="MazesAI automates our brand’s communication, increases conversions, and makes customer experiences seamless across all channels."
        />
        <meta name="twitter:image" content="/logo.png" />
      </Helmet>
      {/* END SEO */}

      <MainNavbar />

      {/* HERO */}
      <section className="ai-hero position-relative overflow-hidden">
        <div className="ai-hero__bg" />
        <Container className="position-relative" style={{ zIndex: 2 }}>
          <Row className="align-items-center gy-5 py-5">
            <Col lg={7}>
              <Badge bg="primary" className="px-3 py-2 me-2 shadow-sm">From {BRAND_NAME}</Badge>
              <span className="text-white-50">Official</span>
              <h1 className="display-4 fw-bold mt-3 text-white">
                Meet {AGENT_NAME}: the way {BRAND_NAME} speaks—clear, warm, and helpful.
              </h1>
              <p className="lead text-white-50 mt-3">
                Our AI agent carries our voice and values into every conversation—welcoming visitors,
                explaining what we do, and guiding next steps with care.
              </p>
              <div className="d-flex flex-wrap gap-3 mt-4">
                <Link to="/chat" className="btn btn-light btn-lg shadow-lg hover-scale">
                  Chat with {AGENT_NAME}
                </Link>
                <a href="#pillars" className="btn btn-outline-light btn-lg shadow-sm hover-scale">
                  How it represents us
                </a>
              </div>
              <div className="d-flex align-items-center gap-4 mt-4 text-white-50">
                <div><strong className="text-white">Human</strong> tone</div>
                <div><strong className="text-white">On-brand</strong> guidance</div>
                <div><strong className="text-white">Always</strong> available</div>
              </div>
            </Col>
            <Col lg={5}>
              <div className="showcase glass-panel rounded-4 p-4">
                <div className="text-white-50 mb-2 small">{AGENT_NAME}</div>
                <div className="preview-slab rounded-3 mb-3" />
                <div className="d-grid gap-2">
                  <Link to="/chat" className="btn btn-primary btn-lg hover-scale">
                    Say Hello
                  </Link>
                  <a href="#moments" className="btn btn-outline-light btn-lg hover-scale">
                    See Moments
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <div className="ai-hero__fade" />
      </section>

      {/* BRAND LINE */}
      <section className="py-4 bg-white border-bottom">
        <Container>
          <div className="text-center text-muted small">
            {BRAND_NAME} believes helpfulness is a product. {AGENT_NAME} keeps us present and consistent.
          </div>
        </Container>
      </section>

      {/* PILLARS */}
      <section id="pillars" className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="fw-bold">How {AGENT_NAME} Represents Us</h2>
            <p className="text-muted">The principles that guide our conversations.</p>
          </div>
          <Row className="row-cols-1 row-cols-md-3 g-4">
            {pillars.map((p, i) => (
              <Col key={i}>
                <Card className="h-100 shadow-card hover-scale card-eq">
                  <Card.Body className="d-flex flex-column">
                    <Card.Title className="fw-bold mb-2">{p.title}</Card.Title>
                    <Card.Text className="text-muted flex-grow-1">{p.text}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* MOMENTS */}
      <section id="moments" className="py-5">
        <Container>
          <Row className="align-items-center gy-4">
            <Col lg={5}>
              <h3 className="fw-bold mb-3">Moments That Matter</h3>
              <p className="text-muted">
                {AGENT_NAME} helps us greet, explain, guide, and celebrate—always in our voice.
              </p>
              <div className="d-flex gap-3 mt-3 flex-wrap">
                <Link to="/chat" className="btn btn-primary shadow-lg hover-scale">
                  Chat Now
                </Link>
                <a href="#faq" className="btn btn-outline-dark shadow-sm hover-scale">
                  Read FAQs
                </a>
              </div>
            </Col>
            <Col lg={7}>
              <Row className="row-cols-1 row-cols-sm-2 g-3">
                {moments.map((m, idx) => (
                  <Col key={idx}>
                    <div className="mini-box p-4 rounded-4 shadow-card hover-scale h-100">
                      <div className="fw-bold mb-2">{m.title}</div>
                      <div className="text-muted">{m.text}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* STORY */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <div className="text-center mb-5">
            <h2 className="fw-bold">Why We Built It</h2>
            <p className="text-muted">
              We wanted every visitor to feel our care—no matter the hour, the channel, or the question.
            </p>
          </div>
          <Row className="row-cols-1 row-cols-md-4 g-4">
            {['Respect People', 'Clarity First', 'Honest Guidance', 'Continuous Care'].map((value, i) => (
              <Col key={i}>
                <div className="step-tile p-4 rounded-4 text-center shadow-card hover-scale h-100">
                  <div className="step-number">{i + 1}</div>
                  <div className="fw-bold mt-2">{value}</div>
                  <div className="text-muted small mt-1">Principles behind {AGENT_NAME}.</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-5">
        <Container>
          <div className="text-center mb-5">
            <h2 className="fw-bold">How People Describe It</h2>
            <p className="text-muted">Reflections from our team and community.</p>
          </div>
          <Row className="row-cols-1 row-cols-md-3 g-4">
            {[
              { name: 'Team Member', quote: 'It sounds like us. That’s the point—and the magic.' },
              { name: 'Customer', quote: 'I felt welcomed and understood. Answers were clear and kind.' },
              { name: 'Partner', quote: 'A voice we trust, available any time we need it.' },
            ].map((t, i) => (
              <Col key={i}>
                <Card className="h-100 shadow-card hover-scale">
                  <Card.Body>
                    <div className="fs-5 mb-3">“{t.quote}”</div>
                    <div className="text-muted">{t.name}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-5 cta-strip text-center text-white">
        <Container>
          <h3 className="fw-bold mb-2">Say hello to {AGENT_NAME}</h3>
          <p className="text-white-50 mb-4">We’re proud of how it represents {BRAND_NAME}.</p>
          <div className="d-flex gap-3 justify-content-center flex-wrap">
            <Link to="/chat" className="btn btn-light btn-lg shadow-lg hover-scale">
              Start a Conversation
            </Link>
            <a href="#faq" className="btn btn-outline-light btn-lg shadow-sm hover-scale">
              Learn More
            </a>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h2 className="fw-bold">Questions We Hear</h2>
            <p className="text-muted">If you need more details, we’re here.</p>
          </div>
          <Row className="justify-content-center">
            <Col lg={8}>
              <Accordion>
                {faqs.map((f, i) => (
                  <Accordion.Item eventKey={String(i)} key={i} className="shadow-sm">
                    <Accordion.Header>{f.q}</Accordion.Header>
                    <Accordion.Body className="text-muted">{f.a}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      <MainFooter />
    </div>
  );
}
